import { Cancel, CheckCircle } from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import {
  GridCellModes,
  GridEditModes,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';

/** Marks boolean cells so the theme can leave the "double-click to edit" inset off them */
export const BOOLEAN_CELL_CLASS = 'boolean-cell';

/**
 * The Data Grid only starts editing on double-click, whose first click gives no feedback at all.
 * An editable boolean cell instead starts edit mode on a single click, where
 * `ToggleBooleanEditCell` flips and commits the value right away, so one click is the whole edit.
 *
 * Editable cells show the editor's own checkbox rather than the read-only icons: the dotted inset
 * the other editable cells wear means "double-click into a field", which a single-click toggle
 * isn't. They opt out of that inset through `BOOLEAN_CELL_CLASS`.
 * @param {import('@mui/x-data-grid-pro').GridRenderCellParams} params
 */
export default function BooleanCell({ colDef, field, id, isEditable = false, value = false }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();

  // Row editing edits every cell of a row at once, so a cell of its own has nothing to start
  if (!isEditable || rootProps.editMode === GridEditModes.Row) {
    // More colorful and visible boolean icons (defaults are gray with no background)
    return value ? <CheckCircle color="success" /> : <Cancel color="error" />;
  }

  return (
    // A real checkbox, so assistive tech reads it as the boolean flip it is. The grid handles
    // keyboard editing on the focused cell (Enter), so the input stays out of the tab order rather
    // than adding a stop of its own to every row.
    <Checkbox
      checked={value}
      size="small"
      slotProps={{ input: { 'aria-label': colDef.headerName, tabIndex: -1 } }}
      // Fills the cell, so the toggle is a comfortable target on touch
      sx={{ width: '100%', height: '100%', borderRadius: 0 }}
      onChange={event => {
        // The second click of a double-click would toggle the value right back. Keyboard-driven
        // changes report a `detail` of 0.
        if (/** @type {MouseEvent} */ (event.nativeEvent).detail > 1) {
          return;
        }
        if (apiRef.current.getCellMode(id, field) === GridCellModes.View) {
          // The value itself is flipped and committed by `ToggleBooleanEditCell`
          apiRef.current.startCellEditMode({ id, field });
        }
      }}
    />
  );
}

BooleanCell.propTypes = {
  colDef: PropTypes.shape({ headerName: PropTypes.string }).isRequired,
  field: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isEditable: PropTypes.bool,
  value: PropTypes.bool,
};
